import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, Path, useFieldArray, useForm } from "react-hook-form";
import {
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

export interface BehaviorEntry {
    key: string;
    current: boolean;
    past: boolean;
    comments: string;
}

export const BEHAVIOR_LIST = [
    { key: "hoarding", label: "Hoarding/Squirreling" },
    { key: "hiding", label: "Hiding items" },
    { key: "breaking", label: "Breaking, throws items" },
    { key: "injuresStaff", label: "Injuries staff/others" },
    { key: "foulLanguage", label: "Uses foul language" },
    { key: "resistiveToCare", label: "Resistive to care" },
    { key: "accusesStealing", label: "Accuses others of stealing" },
    { key: "notSleeping", label: "Not sleeping at night, up when others are sleeping" },
    { key: "wandering", label: "Wandering" },
    { key: "exitSeeking", label: "Exit Seeking" },
    { key: "leftHomeLost", label: "Has left home and gotten lost" },
    { key: "accidentalFires", label: "Accidental fires" },
    { key: "historyArson", label: "History of arson" },
    { key: "unsafeSmoking", label: "Unsafe when smoking" },
    { key: "unsafeCooking", label: "Unsafe cooking — has left stove on" },
    { key: "yelling", label: "Yelling" },
    { key: "screaming", label: "Screaming" },
    { key: "inappropriateNoises", label: "Inappropriate verbal noises" },
    { key: "moodSwings", label: "Mood swings" },
    { key: "manic", label: "Manic" },
    { key: "depressed", label: "Depressed" },
    { key: "criesFrequently", label: "Cries frequently or constantly" },
    { key: "withdrawn", label: "Withdrawn or lethargic" },
    { key: "delusions", label: "Delusions" },
    { key: "hallucinations", label: "Hallucinations" },
    { key: "paranoid", label: "Paranoid" },
    { key: "suicidalThoughts", label: "Suicidal thoughts or behaviors" },
    { key: "injuresSelf", label: "Injuries self" },
    { key: "unrealisticFears", label: "Unrealistic fears or suspicions" },
    { key: "predatorySexual", label: "Predatory sexual behavior" },
    { key: "sexualActingOut", label: "Sexual acting out" },
    { key: "sexualAggression", label: "Sexual aggression" },
    { key: "undressesPublic", label: "Undresses in public to expose self" },
    { key: "aggressive", label: "Aggressive/intimidating" },
    { key: "manipulative", label: "Manipulative" },
    { key: "spitting", label: "Spitting" },
    { key: "verballyAbusive", label: "Verbally abusive" },
    { key: "combative", label: "Combative" },
    { key: "assaultive", label: "Assaultive" },
    { key: "eatsNonEdible", label: "Eats non-edible objects" },
    { key: "inappropriateToileting", label: "Inappropriate toileting activity" },
    { key: "easilyWorried", label: "Easily worried or anxious" },
    { key: "easilyIrritable", label: "Easily irritable/agitated" },
    { key: "demandsAttention", label: "Seeks/demands constant attention/reassurance" },
    { key: "cannotControlBehavior", label: "Inability to control own behavior" },
    { key: "repetitiveComplaints", label: "Repetitive anxious complaints or questions" },
    { key: "obsessiveHealth", label: "Obsessive about health or body functions" },
    { key: "repetitiveMovement", label: "Repetitive physical movement/pacing, hand wringing, fidgeting" },
    { key: "disrobes", label: "Disrobes" },
    { key: "medicationAbuse", label: "Medication abuse or misuse" },
    { key: "drugAlcoholAbuse", label: "Drug or alcohol abuse" },
] as const;

export interface Step12FormValues {
    behaviors: BehaviorEntry[];
    stratTryLater: boolean;
    stratValidateConcerns: boolean;
    stratReportToFamilyMD: boolean;
    stratDescribeInCharting: boolean;
    stratProvideCraft: boolean;
    stratIsolateResident: boolean;
    stratDiscourage: boolean;
    stratReassure: boolean;
    stratProvidePhysical: boolean;
    stratQuietlyInteract: boolean;
    stratRedirect: boolean;
    stratProvideSocial: boolean;
    stratSetFirmLimits: boolean;
    stratDistract: boolean;
    stratOther: string;
    behaviorComments: string;
}

export const DEFAULT_STEP12: Step12FormValues = {
    behaviors: BEHAVIOR_LIST.map(b => ({ key: b.key, current: false, past: false, comments: "" })),
    stratTryLater: false, stratValidateConcerns: false, stratReportToFamilyMD: false,
    stratDescribeInCharting: false, stratProvideCraft: false, stratIsolateResident: false,
    stratDiscourage: false, stratReassure: false, stratProvidePhysical: false,
    stratQuietlyInteract: false, stratRedirect: false, stratProvideSocial: false,
    stratSetFirmLimits: false, stratDistract: false, stratOther: "",
    behaviorComments: "",
};

export interface Step12Props {
    sectionData: Partial<Step12FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step12FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const formatSelected = (items: Array<string | false | null | undefined>) => {
    const selected = items.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return selected.length ? selected.join(", ") : undefined;
};

const Step12BehavioralObservations: React.FC<Step12Props> = ({
    sectionData, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);

    const defaultValues: Step12FormValues = {
        ...DEFAULT_STEP12,
        ...(sectionData ?? {}),
        behaviors:
            sectionData?.behaviors && sectionData.behaviors.length > 0
                ? BEHAVIOR_LIST.map((b, i) => {
                    const found = (sectionData.behaviors ?? []).find(x => x.key === b.key);
                    return found ?? DEFAULT_STEP12.behaviors[i];
                })
                : DEFAULT_STEP12.behaviors.map(b => ({ ...b })),
    };

    const { control, handleSubmit, watch, reset } = useForm<Step12FormValues>({ defaultValues });
    const { fields: behaviorFields } = useFieldArray({ control, name: "behaviors" });

    const onSubmit = async (data: Step12FormValues) => { await onSave(data); };
    const handleSave = async (data: Step12FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Behavioral observations saved.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    const handleSaveAndContinue = async (data: Step12FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    const cb = (name: Path<Step12FormValues>, label: string) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="flex align-items-center gap-2 mb-1">
                <Checkbox inputId={name} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                <label htmlFor={name} className="text-sm cursor-pointer">{label}</label>
            </div>
        )} />
    );

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex flex-wrap justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Behavioral Observations", "Full behavior history shown in read-only mode.", "pi pi-eye")}
                            <div className="p-3">
                                <div className="behavior-observations-grid" style={{ display: "grid", gridTemplateColumns: "30% 20% 50%", gap: 0 }}>
                                    <div className="font-semibold text-sm px-2 py-2" style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>Behavior</div>
                                    <div className="font-semibold text-xs py-2 text-center" style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>Current / Past</div>
                                    <div className="font-semibold text-xs px-2 py-2" style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>Notes</div>

                                    {(values.behaviors ?? []).map((behavior, index) => (
                                        <React.Fragment key={behavior.key || index}>
                                            <div className="px-2 py-2 text-sm" style={{ borderBottom: "1px solid #e5e7eb" }}>
                                                {BEHAVIOR_LIST.find((item) => item.key === behavior.key)?.label ?? behavior.key}
                                            </div>
                                            <div className="py-2 px-2 flex justify-content-center align-items-center gap-2 text-center text-sm" style={{ borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>
                                                <span style={{ color: behavior.current ? "#16a34a" : "#64748b", fontWeight: 600 }} title={`Current: ${behavior.current ? "Yes" : "Not set"}`} aria-label={`Current: ${behavior.current ? "Yes" : "Not set"}`}>
                                                    <span className="mr-1">Current</span>
                                                    {behavior.current ? <i className="pi pi-check" aria-hidden="true" /> : <span aria-hidden="true">-</span>}
                                                </span>
                                                <span style={{ color: behavior.past ? "#16a34a" : "#64748b", fontWeight: 600 }} title={`Past: ${behavior.past ? "Yes" : "Not set"}`} aria-label={`Past: ${behavior.past ? "Yes" : "Not set"}`}>
                                                    <span className="mr-1">Past</span>
                                                    {behavior.past ? <i className="pi pi-check" aria-hidden="true" /> : <span aria-hidden="true">-</span>}
                                                </span>
                                            </div>
                                            <div className="px-2 py-2 text-sm" style={{ borderBottom: "1px solid #e5e7eb", whiteSpace: "pre-wrap" }}>
                                                {behavior.comments || "—"}
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Caregiver Response Strategies", "All selected and unselected strategies are shown below.", "pi pi-check-circle")}
                            <div className="p-3 grid">
                                <InfoRow label="Try activity at a later time" value={values.stratTryLater ? "Yes" : "No"} />
                                <InfoRow label="Validate concerns" value={values.stratValidateConcerns ? "Yes" : "No"} />
                                <InfoRow label="Report behavior to family or MD" value={values.stratReportToFamilyMD ? "Yes" : "No"} />
                                <InfoRow label="Describe behavior in charting" value={values.stratDescribeInCharting ? "Yes" : "No"} />
                                <InfoRow label="Provide craft activities" value={values.stratProvideCraft ? "Yes" : "No"} />
                                <InfoRow label="Isolate resident until behavior improves" value={values.stratIsolateResident ? "Yes" : "No"} />
                                <InfoRow label="Discourage behavior" value={values.stratDiscourage ? "Yes" : "No"} />
                                <InfoRow label="Reassure as needed" value={values.stratReassure ? "Yes" : "No"} />
                                <InfoRow label="Provide physical activities" value={values.stratProvidePhysical ? "Yes" : "No"} />
                                <InfoRow label="Quietly interact with resident" value={values.stratQuietlyInteract ? "Yes" : "No"} />
                                <InfoRow label="Redirect as needed" value={values.stratRedirect ? "Yes" : "No"} />
                                <InfoRow label="Provide social interaction" value={values.stratProvideSocial ? "Yes" : "No"} />
                                <InfoRow label="Set firm limits" value={values.stratSetFirmLimits ? "Yes" : "No"} />
                                <InfoRow label="Distract as needed" value={values.stratDistract ? "Yes" : "No"} />
                                <InfoRow label="Other Strategy" value={values.stratOther} />
                                <InfoRow label="General Comments" value={values.behaviorComments} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* Behavior Table */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Behavioral Observations", "Indicate whether each behavior is current, has occurred in the past, and add notes.", "pi pi-eye")}
                            <div className="p-3">
                                {/* Grid: 1fr label | 100px Current | 100px Past */}
                                <div className="behavior-observations-grid" style={{ display: "grid", gridTemplateColumns: "30% 20% 50%", gap: 0 }}>
                                    {/* Header row */}
                                    <div className="font-semibold text-sm px-2 py-2" style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>Behavior</div>
                                    <div className="font-semibold text-xs py-2 text-center" style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>Current / Past</div>
                                    <div className="font-semibold text-sm px-2 py-2" style={{ background: "#f8fafc", borderBottom: "2px solid #e5e7eb" }}>Notes</div>

                                    {behaviorFields.map((field, index) => (
                                        <React.Fragment key={field.id}>
                                            {/* Label */}
                                            <div className="px-2 text-sm align-self-center" style={{ lineHeight: 1.35, paddingTop: "0.75rem", paddingBottom: "0.25rem" }}>
                                                {BEHAVIOR_LIST[index].label}
                                            </div>
                                            {/* Current checkbox */}
                                            <div className="flex flex-row flex-nowrap justify-content-center align-items-center gap-2 px-2" style={{ paddingTop: "0.75rem", paddingBottom: "0.25rem" }}>
                                                <div className="flex align-items-center gap-1">
                                                    <Controller
                                                        name={`behaviors.${index}.current`}
                                                        control={control}
                                                        render={({ field: f }) => (
                                                            <Checkbox inputId={`behavior-${index}-current`} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                                                        )}
                                                    />
                                                    <label htmlFor={`behavior-${index}-current`} className="text-xs cursor-pointer">Current</label>
                                                </div>
                                                <div className="flex align-items-center gap-1">
                                                    <Controller
                                                        name={`behaviors.${index}.past`}
                                                        control={control}
                                                        render={({ field: f }) => (
                                                            <Checkbox inputId={`behavior-${index}-past`} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                                                        )}
                                                    />
                                                    <label htmlFor={`behavior-${index}-past`} className="text-xs cursor-pointer">Past</label>
                                                </div>
                                            </div>
                                            {/* Past checkbox */}
                                            {/* Notes — spans all 3 columns */}
                                            <div className="px-2" style={{ borderBottom: "1px solid #e5e7eb", paddingTop: "0.75rem", paddingBottom: "0.75rem" }}>
                                                <Controller
                                                    name={`behaviors.${index}.comments`}
                                                    control={control}
                                                    render={({ field: f }) => (
                                                        <InputTextarea
                                                            {...f}
                                                            className="w-full"
                                                            rows={1}
                                                            autoResize
                                                            placeholder="Notes"
                                                            style={{ fontSize: "0.82rem", minHeight: "2.2rem", resize: "none" }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </Card>

                        {/* Caregiver Strategies */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Caregiver Response Strategies", "If the client displays significant or emotional behaviors, caregiver will:", "pi pi-check-circle")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-12 md:col-6">{cb("stratTryLater", "Try activity at a later time")}</div>
                                    <div className="col-12 md:col-6">{cb("stratValidateConcerns", "Validate concerns")}</div>
                                    <div className="col-12 md:col-6">{cb("stratReportToFamilyMD", "Report behavior to family or MD")}</div>
                                    <div className="col-12 md:col-6">{cb("stratDescribeInCharting", "Describe behavior in charting")}</div>
                                    <div className="col-12 md:col-6">{cb("stratProvideCraft", "Provide craft activities")}</div>
                                    <div className="col-12 md:col-6">{cb("stratIsolateResident", "Isolate resident until behavior improves")}</div>
                                    <div className="col-12 md:col-6">{cb("stratDiscourage", "Discourage behavior")}</div>
                                    <div className="col-12 md:col-6">{cb("stratReassure", "Reassure as needed")}</div>
                                    <div className="col-12 md:col-6">{cb("stratProvidePhysical", "Provide physical activities")}</div>
                                    <div className="col-12 md:col-6">{cb("stratQuietlyInteract", "Quietly interact with resident")}</div>
                                    <div className="col-12 md:col-6">{cb("stratRedirect", "Redirect as needed")}</div>
                                    <div className="col-12 md:col-6">{cb("stratProvideSocial", "Provide social interaction")}</div>
                                    <div className="col-12 md:col-6">{cb("stratSetFirmLimits", "Set firm limits")}</div>
                                    <div className="col-12 md:col-6">{cb("stratDistract", "Distract as needed")}</div>
                                    <div className="col-12">
                                        <label className="font-medium text-sm block mb-1">Other strategy</label>
                                        <Controller name="stratOther" control={control} render={({ field: f }) => (
                                            <InputText {...f} className="w-full" placeholder="Describe other strategy" />
                                        )} />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <label className="font-medium text-sm block mb-1">General Comments</label>
                                    <Controller name="behaviorComments" control={control} render={({ field: f }) => (
                                        <InputTextarea {...f} className="w-full" rows={3} autoResize placeholder="General behavioral observations and comments" />
                                    )} />
                                </div>
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex flex-wrap justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step12BehavioralObservations;
